import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { LoginComponent } from "@app/account/login/login.component";
import { ResetPasswordComponent } from "@app/account/reset-password/reset-password.component";
import { ForgotPasswordComponent } from "@app/account/forgot-password/forgot-password.component";
import { ProfileComponent } from "@app/account/profile/profile.component";
import { IsAuthenticatedGuard } from "@app/account/is-authenticated.guard";
import { NewPasswordRequiredComponent } from "@app/account/new-password-required/new-password-required.component";
import { ViewFrameComponent } from "@scaffold/routes/view-frame/view-frame.component";
import { EditFrameComponent } from "@scaffold/routes/edit-frame/edit-frame.component";
import { ModelTypeListComponent } from "./data-model/type-list/type-list.component";
import { ModelDetailsComponent } from "./data-model/model-details/model-details.component";
import { KeyItemsComponent } from "./data-model/key-items/key-items.component";
import { DataListComponent } from "@scaffold/routes/data-list/data-list.component";
import { AdminConsoleComponent } from "./admin/admin-console/admin-console.component";
import { VerifyPhoneAndEmailComponent } from "./account/verify-phone-and-email/verify-phone-and-email.component";
import { DataModelPrefsComponent } from "./data-model/data-model-prefs/data-model-prefs.component";
import { PendingChangesGuard } from "@app/shared/pending-changes.guard";
import { LtiCallbackComponent } from "./utility/lti-callback/lti-callback.component";
import { LtiFrameComponent } from "./utility/lti-frame/lti-frame.component";
import { RolePermissionsGuard } from "./account/role-permissions.guard";
import { ChangelogComponent } from "./changes/changelog/changelog.component";
import { CourseSelectComponent } from "@scaffold/routes/course-select/course-select.component";
import { CourseCreateComponent } from "@scaffold/routes/course-create/course-create.component";
import { LandingPageComponent } from "@scaffold/routes/landing-page/landing-page.component";
import { NewDataComponent } from "@scaffold/routes/new-data/new-data.component";
import { SecurityComponent } from "./security/security.component";
import { AccountSelectComponent } from "@scaffold/routes/account-select/account-select.component";
import { RouteGuardService } from "@services/route-guard.service";
import { AiDetectionRootComponent } from "@scaffold/ai-detection-root/ai-detection-root.component";
import { HomeGuard } from "@services/home.guard";

const routes: Routes = [
  {
    path: "sso",
    loadChildren: () => import("./sso/sso.module").then((m) => m.SsoModule),
  },
  { path: "login", component: LoginComponent },
  { path: "direct-login", component: LoginComponent },
  { path: "forgot-password", component: ForgotPasswordComponent },
  { path: "new-password-required", component: NewPasswordRequiredComponent },
  { path: "verify-phone-and-email", component: VerifyPhoneAndEmailComponent },
  // Everything in this block will need to be authenticated to navigate too
  {
    path: "",
    canActivateChild: [IsAuthenticatedGuard],
    children: [
      { path: "", redirectTo: "/login", pathMatch: "full" },
      {
        path: "admin",
        canActivate: [RolePermissionsGuard],
        data: { permission: ["view_admin"] },
        loadChildren: () =>
          import("./admin/admin.module").then((m) => m.AdminModule),
      },
      // {
      //   path: "admin",
      //   component: AdminConsoleComponent,
      //   data: { permission: ["view_admin"] },
      //   canActivate: [RolePermissionsGuard],
      // },
      {
        path: "batch-details",
        loadChildren: () =>
          import("./batch-details/batch-details.module").then(
            (m) => m.BatchDetailsModule
          ),

        // data: { toMatch: /datalink/ },
        // canActivate: [RouteGuardService],
      },
      { path: "reset-password", component: ResetPasswordComponent },
      { path: "profile", component: ProfileComponent },
      {
        path: "documentation",
        loadChildren: () =>
          import("./datax/datax.module").then((m) => m.DataxModule),
        data: { toMatch: /datax/ },
        canActivate: [RouteGuardService],
      },
      {
        path: "migration",
        loadChildren: () =>
          import("./migration/migration.module").then((m) => m.MigrationModule),
      },
      {
        path: "data-models",
        component: ModelTypeListComponent,
        children: [
          { path: "preferences", component: DataModelPrefsComponent },
          { path: ":id", component: ModelDetailsComponent },
          { path: ":id/:key/components", component: KeyItemsComponent },
        ],
        data: { permission: ["manage_views"] },
        canActivate: [RolePermissionsGuard],
      },
      {
        path: "ai-detection-report",
        component: AiDetectionRootComponent,
        data: { toMatch: /ai-detection/ },
        canActivate: [RouteGuardService],
      },

      { path: "lticallback/:id", component: LtiCallbackComponent },
      { path: "external/:course/display", component: LtiFrameComponent },
      { path: "changes", component: ChangelogComponent },
      { path: "security", component: SecurityComponent },
      {
        path: "home",
        component: LandingPageComponent,
        canActivate: [HomeGuard],
      },

      {
        path: "accounts",
        component: AccountSelectComponent,
        data: { toMatch: /archivev2/ },
        canActivate: [RouteGuardService],
      },
      { path: "courses", component: CourseSelectComponent },
      { path: "courses/create", component: CourseCreateComponent },
      // setting up users/create to eventually work with all users/* path, assuming it will eventually require non config components there
      {
        path: "users/create",
        loadChildren: () =>
          import("./users/users.module").then((m) => m.UsersModule),
          canActivate: [RolePermissionsGuard],
      },
      

      {
        path: "views/:id",
        component: ViewFrameComponent,
        data: { allow_edit: true, root: true },
      },
      {
        path: "views/:id/edit",
        component: EditFrameComponent,
        canDeactivate: [PendingChangesGuard],
        data: { permission: ["manage_views"] },
        canActivate: [RolePermissionsGuard],
      },
      {
        path: ":type",
        component: DataListComponent,
        data: {
          permission: ["manage_users", "manage_tools"],
          rest_type: { manage_users: ["users", "roles"], manage_tools: "lti" },
        },
        canActivate: [RolePermissionsGuard],
      },
      { path: ":type/create", component: NewDataComponent },
      {
        path: ":type/:id",
        component: ViewFrameComponent,
        data: {
          root: true,
          permission: ["manage_users", "manage_tools"],
          rest_type: { manage_users: ["users", "roles"], manage_tools: "lti" },
        },
        canDeactivate: [PendingChangesGuard],
        canActivate: [RolePermissionsGuard],
        children: [
          {
            path: ":type",
            component: ViewFrameComponent,
            canDeactivate: [PendingChangesGuard],
          },
          {
            path: ":type/:id",
            component: ViewFrameComponent,
            canDeactivate: [PendingChangesGuard],
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { useHash: false /*, enableTracing: true */ }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
